import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	InputBase,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { fetchNotifications, saveNotifications } from "actions/notificationActions";
import { fetchUsers } from "actions/userActions";
import { PrimaryButton } from "components/Common/Buttons/Button";
import {
	addUserNotification,
	removeUserNotification,
	selectAddedUserIds,
	selectDeletedUserIds,
	selectNotifications,
	selectNotificationUsers,
	selectOriginalUsers,
	selectUpdatedUserIds,
	updateUserNotificationSettings,
} from "features/notifications";
import { selectUsers, usersSelectors } from "features/users";
import { useApiOnce, useAppSelector, useAuth } from "hooks/hooks";
import React, { useMemo, useState } from "react";
import { NotificationSettings, User, UserNotificationData } from "types";
import useRole from "utilities/roleUtils/roleCheck";
import styles from "./CompanyNotifications.module.scss";

export const CompanyNotifications = () => {
	const { dispatch, auth } = useAuth();
	const { isActionRole } = useRole();
	const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
	const [userSearchTerm, setUserSearchTerm] = useState("");
	const [unsavedChanges, setUnsavedChanges] = useState(false);

	const notificationState = useAppSelector(selectNotifications);
	const notificationUsers = useAppSelector(selectNotificationUsers);
	const addedUserIds = useAppSelector(selectAddedUserIds);
	const updatedUserIds = useAppSelector(selectUpdatedUserIds);
	const deletedUserIds = useAppSelector(selectDeletedUserIds);
	const originalUsers = useAppSelector(selectOriginalUsers);
	const allUsers = useAppSelector(usersSelectors.selectAll);

	useApiOnce(fetchNotifications, notificationState);
	useApiOnce(fetchUsers, useAppSelector(selectUsers));

	// Track unsaved changes based on tracked user changes
	React.useEffect(() => {
		const hasChanges =
			addedUserIds.length > 0 || updatedUserIds.length > 0 || deletedUserIds.length > 0;
		setUnsavedChanges(hasChanges);
	}, [addedUserIds, updatedUserIds, deletedUserIds]);

	const handleNotificationChange = (
		userId: string,
		notificationType: keyof NotificationSettings,
		checked: boolean,
	) => {
		const currentUser = notificationUsers.find((user) => user.userId === userId);
		if (currentUser) {
			const updatedSettings = {
				...currentUser.settings,
				[notificationType]: checked,
			};
			dispatch(updateUserNotificationSettings({ userId, settings: updatedSettings }));
		}
	};

	const handleRemoveUser = (userId: string) => {
		dispatch(removeUserNotification(userId));
	};

	const handleAddUser = (user: User) => {
		const userNotificationData: UserNotificationData = {
			userId: user.id,
			displayName: user.displayName,
			mail: user.mail || user.userPrincipalName,
			settings: {
				newsletter: false,
				releaseNote: false,
				recommendation: false,
				alert: false,
			},
		};
		dispatch(addUserNotification(userNotificationData));
		setIsAddUserDialogOpen(false);
	};

	const handleSave = async () => {
		try {
			// Build operations array with only changed users
			const operations: any[] = [];

			// Add operations for added users
			addedUserIds.forEach((userId) => {
				const user = notificationUsers.find((u) => u.userId === userId);
				if (user) {
					operations.push({
						operation: "add",
						userId: user.userId,
						displayName: user.displayName,
						mail: user.mail,
						settings: {
							newsletter: user.settings.newsletter,
							releaseNote: user.settings.releaseNote,
							recommendation: user.settings.recommendation,
							alert: user.settings.alert,
						},
					});
				}
			});

			// Add operations for updated users
			updatedUserIds.forEach((userId) => {
				const user = notificationUsers.find((u) => u.userId === userId);
				if (user) {
					operations.push({
						operation: "update",
						userId: user.userId,
						displayName: user.displayName,
						mail: user.mail,
						settings: {
							newsletter: user.settings.newsletter,
							releaseNote: user.settings.releaseNote,
							recommendation: user.settings.recommendation,
							alert: user.settings.alert,
						},
					});
				}
			});

			// Add operations for deleted users
			deletedUserIds.forEach((userId) => {
				const originalUser = originalUsers[userId];
				operations.push({
					operation: "delete",
					userId: userId,
					displayName: originalUser?.displayName || "",
				});
			});

			// Only send if there are changes
			if (operations.length > 0) {
				await dispatch(saveNotifications({ auth, body: operations }));
			}
		} catch (error) {
			console.error("Failed to save notifications:", error);
		}
	};

	const openAddUserDialog = () => {
		setUserSearchTerm(""); // Reset search when opening dialog
		setIsAddUserDialogOpen(true);
	};

	// Get available users from Redux state with search and sorting
	const availableUsers = useMemo(() => {
		return allUsers
			.filter((user: User) => {
				// Filter out users already in notifications
				const isAlreadyAdded = notificationUsers.some(
					(notifUser) => notifUser.userId === user.id,
				);
				if (isAlreadyAdded) {
					return false;
				}

				// Filter by search term
				if (userSearchTerm.trim() === "") {
					return true;
				}

				const searchLower = userSearchTerm.toLowerCase();
				const nameMatch = user.displayName?.toLowerCase().includes(searchLower);
				const emailMatch =
					user.mail?.toLowerCase().includes(searchLower) ||
					user.userPrincipalName?.toLowerCase().includes(searchLower);

				return nameMatch || emailMatch;
			})
			.sort((a, b) => {
				// Sort alphabetically by display name
				return (a.displayName || "").localeCompare(b.displayName || "");
			});
	}, [allUsers, notificationUsers, userSearchTerm]);

	return (
		<Grid container className={styles.container}>
			<Grid item xs={12}>
				<div className={styles.header}>
					<Typography variant="h3" fontWeight={500}>
						Notifications
					</Typography>
					{isActionRole && (
						<div className={styles.actions}>
							<Button
								variant="outlined"
								startIcon={<AddIcon />}
								onClick={openAddUserDialog}
								className={styles.addButton}
							>
								Add User
							</Button>
							<PrimaryButton
								text="Save Changes"
								size="medium"
								variantStyle="contained"
								action={handleSave}
								isDisabled={!unsavedChanges || notificationState.isSaving}
								isLoading={notificationState.isSaving}
							/>
						</div>
					)}
				</div>

				<TableContainer className={styles.tableContainer}>
					<Table aria-label="notifications table">
						<TableHead>
							<TableRow>
								<TableCell>User</TableCell>
								<TableCell align="center">Newsletter</TableCell>
								<TableCell align="center">Changes/Release Notes</TableCell>
								<TableCell align="center">Recommendations</TableCell>
								<TableCell align="center">Alarms</TableCell>
								{isActionRole && <TableCell align="center">Actions</TableCell>}
							</TableRow>
						</TableHead>
						<TableBody>
							{notificationState.isLoading ? (
								Array.from(new Array(5)).map((_, index) => (
									<TableRow key={index}>
										<TableCell>
											<Skeleton width="100%" />
										</TableCell>
										<TableCell>
											<Skeleton width="100%" />
										</TableCell>
										<TableCell>
											<Skeleton width="100%" />
										</TableCell>
										<TableCell>
											<Skeleton width="100%" />
										</TableCell>
										<TableCell>
											<Skeleton width="100%" />
										</TableCell>
										{isActionRole && (
											<TableCell>
												<Skeleton width="100%" />
											</TableCell>
										)}
									</TableRow>
								))
							) : notificationUsers.length > 0 ? (
								notificationUsers.map((user) => (
									<TableRow key={user.userId} className={styles.tableRow}>
										<TableCell>
											<div className={styles.userInfo}>
												<div>
													<Typography variant="body1" fontWeight={500}>
														{user.displayName}
													</Typography>
													<Typography
														variant="body2"
														color="textSecondary"
													>
														{user.mail}
													</Typography>
												</div>
											</div>
										</TableCell>
										<TableCell align="center">
											<Checkbox
												checked={user.settings.newsletter}
												onChange={(e) =>
													handleNotificationChange(
														user.userId,
														"newsletter",
														e.target.checked,
													)
												}
												disabled={!isActionRole}
											/>
										</TableCell>
										<TableCell align="center">
											<Checkbox
												checked={user.settings.releaseNote}
												onChange={(e) =>
													handleNotificationChange(
														user.userId,
														"releaseNote",
														e.target.checked,
													)
												}
												disabled={!isActionRole}
											/>
										</TableCell>
										<TableCell align="center">
											<Checkbox
												checked={user.settings.recommendation}
												onChange={(e) =>
													handleNotificationChange(
														user.userId,
														"recommendation",
														e.target.checked,
													)
												}
												disabled={!isActionRole}
											/>
										</TableCell>
										<TableCell align="center">
											<Checkbox
												checked={user.settings.alert}
												onChange={(e) =>
													handleNotificationChange(
														user.userId,
														"alert",
														e.target.checked,
													)
												}
												disabled={!isActionRole}
											/>
										</TableCell>
										{isActionRole && (
											<TableCell align="center">
												<IconButton
													onClick={() => handleRemoveUser(user.userId)}
													size="small"
													color="error"
												>
													<DeleteIcon />
												</IconButton>
											</TableCell>
										)}
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell
										colSpan={isActionRole ? 6 : 5}
										align="center"
										className={styles.emptyState}
									>
										<Typography variant="body1" color="textSecondary">
											No users configured for notifications
										</Typography>
										{isActionRole && (
											<PrimaryButton
												text="Add First User"
												size="medium"
												variantStyle="contained"
												action={openAddUserDialog}
												icon={<AddIcon />}
												className={styles.emptyStateButton}
											/>
										)}
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Grid>

			{/* Add User Dialog */}
			<Dialog
				open={isAddUserDialogOpen}
				onClose={() => setIsAddUserDialogOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Add User to Notifications</DialogTitle>
				<DialogContent>
					<div className={styles.searchContainer}>
						<div className={styles.searchField}>
							<SearchIcon className={styles.searchIcon} />
							<InputBase
								placeholder="Search users by name or email..."
								value={userSearchTerm}
								onChange={(e) => setUserSearchTerm(e.target.value)}
								className={styles.searchInput}
								fullWidth
							/>
						</div>
					</div>
					<div className={styles.usersList}>
						{availableUsers.length > 0 ? (
							availableUsers.map((user) => (
								<div
									key={user.id}
									className={styles.userOption}
									onClick={() => handleAddUser(user)}
								>
									<div className={styles.userInfo}>
										<Typography variant="body1" fontWeight={500}>
											{user.displayName}
										</Typography>
										<Typography variant="body2" color="textSecondary">
											{user.mail || user.userPrincipalName}
										</Typography>
									</div>
								</div>
							))
						) : (
							<Typography variant="body2" color="textSecondary" align="center">
								{userSearchTerm.trim()
									? `No users found matching "${userSearchTerm}"`
									: "No available users to add"}
							</Typography>
						)}
					</div>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIsAddUserDialogOpen(false)}>Cancel</Button>
				</DialogActions>
			</Dialog>
		</Grid>
	);
};
